import { getStorageValue } from '@/hooks/browser/use-local-storage';
import { AccountFeatureFlag, accountHasFeatureFlag } from '@/services/auth/feature-flags';
import type { AugmentedAccount, User } from '@/services/auth/types';

// The app shell loads this module for everyone, so it holds only what decides whether the web push
// runtime loads at all; the runtime itself stays in a lazy chunk.

// Set once this browser has loaded the web push instance, so a closed gate only touches service
// workers for people who opted in before; everyone else's browser is left exactly as on main.
export const REGISTERED_KEY = 'cio-web-push-alerts:registered';

export type GateAccount = Pick<AugmentedAccount, 'feature_flags'> | null | undefined;
export type GateUser = Pick<User, 'can_masquerade' | 'user_flags'> | null | undefined;
export type LayoutAccount = (GateAccount & { id: number; is_masq: boolean }) | null | undefined;
export type LayoutUser = (GateUser & { id: number }) | null | undefined;

/**
 * Every client-side lock: the `web-push-alerts` flag for this user, staff, and the user's own opt-in
 * on Experimental features. Services re-checks all of them and the house config.
 */
export function isWebPushAlertsOn(account: GateAccount, user: GateUser): boolean {
  return (
    accountHasFeatureFlag(account, AccountFeatureFlag.WebPushAlerts) &&
    user?.can_masquerade === true &&
    user.user_flags?.web_push_alerts === true
  );
}

/**
 * Whether this browser kept a web push setup from an earlier session, which a closed gate undoes.
 * The shell reads this on every load, so blocked storage must read as no setup, not throw.
 */
export function hasWebPushAlertsSetup(): boolean {
  return getStorageValue<unknown>(REGISTERED_KEY, null) != null;
}
